import React, { useEffect, useRef, useState } from "react";
import { addTimestampToFile } from "@thdinc/hpf-shared";
import { FaVideo, FaRegFileAlt, FaLink, FaCloudUploadAlt, FaChevronDown } from "react-icons/fa";
import FormModal from "@/components/modals/FormModal";
import Alert from "@/components/alert/Alert";
import ToggleInput from "@/components/toggle/Toggle";
import { POST, PUT } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { s3UploadResourceFile } from "@/helpers/FileFunctions";
import { useAppSelector } from "@/hooks/hook";
import { extractYoutubeId, extractVimeoId, fetchVimeoMeta, extractLoomId, fetchLoomMeta } from "../utils";
import { ApiResource } from "../interface";

declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: (() => void) | undefined;
    }
}

type ResourceKind = "Video" | "Document";

interface AddResourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    editingResource?: ApiResource | null;
}

const videoCategories = ["Culture", "Route Guide", "Operations", "Logistics", "Safety", "Sales", "Health"];
const documentCategories = ["Handbook", "Safety", "Template", "Policy", "Client-facing", "Directory"];

const inputClass =
    "w-full px-4 py-3 bg-p-gray rounded-xl focus:outline-none focus:ring-1 focus:ring-primary-red text-sm text-gray-700 placeholder:text-gray-400";
const labelClass = "text-xs font-semibold text-gray-600 mb-1.5 block";

const formatDuration = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const paddedSeconds = String(seconds).padStart(2, "0");
    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`;
    }
    return `${minutes}:${paddedSeconds}`;
};

const ACCEPTED_DOCUMENT_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const MAX_DOCUMENT_SIZE_MB = 25;

const validateDocumentFile = (file: File): string | null => {
    const isAcceptedType =
        ACCEPTED_DOCUMENT_TYPES.includes(file.type) || /\.(pdf|docx|xlsx|pptx)$/i.test(file.name);
    if (!isAcceptedType) return "Please select a PDF, DOCX, XLSX or PPTX file.";
    if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) return `File size exceeds ${MAX_DOCUMENT_SIZE_MB} MB.`;
    return null;
};

const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

let youtubeApiPromise: Promise<void> | null = null;
const loadYoutubeIframeApi = (): Promise<void> => {
    if (window.YT?.Player) return Promise.resolve();
    if (!youtubeApiPromise) {
        youtubeApiPromise = new Promise((resolve) => {
            const previousCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                previousCallback?.();
                resolve();
            };
            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            document.body.appendChild(script);
        });
    }
    return youtubeApiPromise;
};

const emptyVideoForm = { link: "", title: "", category: videoCategories[0], duration: "", description: "", isActive: true };
const emptyDocumentForm = {
    file: null as File | null,
    fileName: "",
    title: "",
    category: documentCategories[0],
    description: "",
    isActive: true,
};

const AddResourceModal: React.FC<AddResourceModalProps> = ({ isOpen, onClose, onSuccess, editingResource }) => {
    const { refreshToken } = useAppSelector((store) => store.user);
    const { addResourceApi } = API_ROUTES;
    const isEditing = Boolean(editingResource);

    const [activeType, setActiveType] = useState<ResourceKind>("Video");
    const [videoForm, setVideoForm] = useState(emptyVideoForm);
    const [documentForm, setDocumentForm] = useState(emptyDocumentForm);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isFetchingDuration, setIsFetchingDuration] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);

    // Prefill the form when opening in edit mode, reset when opening fresh
    useEffect(() => {
        if (!isOpen) return;
        if (editingResource) {
            setActiveType(editingResource.type);
            if (editingResource.type === "Video") {
                setVideoForm({
                    link: editingResource.url,
                    title: editingResource.name,
                    category: editingResource.category,
                    duration: editingResource.duration || "",
                    description: editingResource.description,
                    isActive: editingResource.is_active,
                });
            } else {
                setDocumentForm({
                    file: null,
                    fileName: "",
                    title: editingResource.name,
                    category: editingResource.category,
                    description: editingResource.description,
                    isActive: editingResource.is_active,
                });
            }
        } else {
            setActiveType("Video");
            setVideoForm(emptyVideoForm);
            setDocumentForm(emptyDocumentForm);
        }
    }, [isOpen, editingResource]);

    const videoCategoryOptions =
        videoForm.category && !videoCategories.includes(videoForm.category)
            ? [...videoCategories, videoForm.category]
            : videoCategories;
    const documentCategoryOptions =
        documentForm.category && !documentCategories.includes(documentForm.category)
            ? [...documentCategories, documentForm.category]
            : documentCategories;

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const youtubeId = extractYoutubeId(videoForm.link);
    const vimeoId = !youtubeId ? extractVimeoId(videoForm.link) : null;
    const loomId = !youtubeId && !vimeoId ? extractLoomId(videoForm.link) : null;

    // YouTube: thumbnail is a predictable URL, duration comes from the IFrame Player API
    useEffect(() => {
        if (!youtubeId) {
            if (!vimeoId && !loomId) setThumbnailUrl(null);
            setIsFetchingDuration(false);
            return;
        }

        setThumbnailUrl(`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`);

        let cancelled = false;
        setIsFetchingDuration(true);

        loadYoutubeIframeApi().then(() => {
            if (cancelled || !playerContainerRef.current) return;
            playerRef.current?.destroy?.();
            playerRef.current = new window.YT.Player(playerContainerRef.current, {
                videoId: youtubeId,
                events: {
                    onReady: (event: any) => {
                        if (cancelled) return;
                        const seconds = event.target.getDuration();
                        if (seconds > 0) {
                            setVideoForm((f) => ({ ...f, duration: formatDuration(seconds) }));
                        }
                        setIsFetchingDuration(false);
                    },
                    onError: () => {
                        if (!cancelled) setIsFetchingDuration(false);
                    },
                },
            });
        });

        return () => {
            cancelled = true;
        };
    }, [youtubeId]);

    // Vimeo: both thumbnail and duration come from Vimeo's public oEmbed endpoint (no API key)
    useEffect(() => {
        if (!vimeoId) return;

        let cancelled = false;
        setThumbnailUrl(null);
        setIsFetchingDuration(true);

        fetchVimeoMeta(videoForm.link.trim()).then((meta) => {
            if (cancelled) return;
            setThumbnailUrl(meta?.thumbnailUrl ?? null);
            if (meta?.durationSeconds) {
                setVideoForm((f) => ({ ...f, duration: formatDuration(meta.durationSeconds as number) }));
            }
            setIsFetchingDuration(false);
        });

        return () => {
            cancelled = true;
        };
    }, [vimeoId]);

    // Loom: both thumbnail and duration come from Loom's public oEmbed endpoint (no API key)
    useEffect(() => {
        if (!loomId) return;

        let cancelled = false;
        setThumbnailUrl(null);
        setIsFetchingDuration(true);

        fetchLoomMeta(videoForm.link.trim()).then((meta) => {
            if (cancelled) return;
            setThumbnailUrl(meta?.thumbnailUrl ?? null);
            if (meta?.durationSeconds) {
                setVideoForm((f) => ({ ...f, duration: formatDuration(meta.durationSeconds as number) }));
            }
            setIsFetchingDuration(false);
        });

        return () => {
            cancelled = true;
        };
    }, [loomId]);

    useEffect(() => {
        return () => {
            playerRef.current?.destroy?.();
        };
    }, []);

    const closeAndReset = () => {
        setVideoForm(emptyVideoForm);
        setDocumentForm(emptyDocumentForm);
        onClose();
    };

    const handleSubmit = async () => {
        if (activeType === "Video") {
            if (!videoForm.link.trim()) return Alert({ type: "error", message: "Video link is required" });
            if (!videoForm.title.trim()) return Alert({ type: "error", message: "Title is required" });
            if (!videoForm.description.trim()) return Alert({ type: "error", message: "Description is required" });

            const normalizedUrl = /^https?:\/\//i.test(videoForm.link.trim())
                ? videoForm.link.trim()
                : `https://${videoForm.link.trim()}`;

            setIsSubmitting(true);
            try {
                const payload = {
                    name: videoForm.title.trim(),
                    type: "Video",
                    description: videoForm.description.trim(),
                    category: videoForm.category,
                    duration: videoForm.duration || undefined,
                    url: normalizedUrl,
                    is_active: videoForm.isActive,
                };
                const res = isEditing
                    ? await PUT(`${addResourceApi}/${editingResource?.id}`, payload)
                    : await POST(addResourceApi, payload);
                Alert({ message: res?.data?.message ?? (isEditing ? "Video updated" : "Video added") });
                closeAndReset();
                onSuccess?.();
            } catch (error: any) {
                Alert({
                    type: "error",
                    message: error?.response?.data?.message ?? `Unable to ${isEditing ? "update" : "add"} video`,
                });
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        if (!isEditing && !documentForm.file) {
            return Alert({ type: "error", message: "Please choose a file to upload" });
        }
        if (!documentForm.title.trim()) return Alert({ type: "error", message: "Title is required" });
        if (!documentForm.description.trim()) return Alert({ type: "error", message: "Description is required" });

        setIsSubmitting(true);
        try {
            let url = editingResource?.url;
            let size = editingResource?.size ?? undefined;

            if (documentForm.file) {
                const uploadRes = await s3UploadResourceFile(addTimestampToFile(documentForm.file), refreshToken as string);
                if (!uploadRes?.data?.success || !uploadRes?.data?.url) {
                    throw new Error(uploadRes?.data?.message || "File upload failed");
                }
                url = uploadRes.data.url;
                size = formatFileSize(documentForm.file.size);
            }

            const payload = {
                name: documentForm.title.trim(),
                type: "Document",
                description: documentForm.description.trim(),
                category: documentForm.category,
                size,
                url,
                is_active: documentForm.isActive,
            };
            const res = isEditing
                ? await PUT(`${addResourceApi}/${editingResource?.id}`, payload)
                : await POST(addResourceApi, payload);
            Alert({ message: res?.data?.message ?? (isEditing ? "Document updated" : "Document added") });
            closeAndReset();
            onSuccess?.();
        } catch (error: any) {
            Alert({
                type: "error",
                message: error?.response?.data?.message ?? error?.message ?? `Unable to ${isEditing ? "update" : "add"} document`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={closeAndReset}
            modalTitle={
                isEditing
                    ? activeType === "Video" ? "Edit video" : "Edit document"
                    : activeType === "Video" ? "Add a video" : "Add a document"
            }
            width="lg:w-2/6 md:w-3/6 w-full"
            overflow="overflow-hidden"
            customHeader={
                <p className="text-xs text-gray-500 px-8 pb-4 -mt-2 bg-p-gray">
                    {isEditing
                        ? activeType === "Video"
                            ? "Update this video's details."
                            : "Update this document's details."
                        : activeType === "Video"
                            ? "Link a training video and add its details for the team."
                            : "Upload a document and tell the team what it's for."}
                </p>
            }
            footer={
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={closeAndReset}
                        disabled={isSubmitting}
                        className="px-5 py-2 rounded-full text-sm font-medium text-gray-600 border border-gray-200 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-5 py-2 rounded-full text-sm font-medium bg-primary-red hover:bg-primary-red-button-hover text-white whitespace-nowrap disabled:opacity-60"
                    >
                        {isSubmitting
                            ? "Saving..."
                            : isEditing
                                ? "Save changes"
                                : `+ Add ${activeType === "Video" ? "video" : "document"}`}
                    </button>
                </div>
            }
        >
            <div className="flex gap-2 mb-5">
                <button
                    type="button"
                    disabled={isEditing}
                    onClick={() => setActiveType("Video")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-semibold transition ${activeType === "Video" ? "bg-primary-red text-white" : "bg-p-gray text-gray-600"
                        } ${isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                    <FaVideo size={13} /> Video
                </button>
                <button
                    type="button"
                    disabled={isEditing}
                    onClick={() => setActiveType("Document")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-semibold transition ${activeType === "Document" ? "bg-primary-red text-white" : "bg-p-gray text-gray-600"
                        } ${isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                    <FaRegFileAlt size={13} /> Document
                </button>
            </div>

            {activeType === "Video" ? (
                <div className="flex flex-col gap-4">
                    <div>
                        <p className={labelClass}>Video link</p>
                        <div className="relative">
                            <FaLink className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                            <input
                                type="text"
                                value={videoForm.link}
                                onChange={(e) => setVideoForm((f) => ({ ...f, link: e.target.value }))}
                                placeholder="Paste a YouTube, Vimeo, Loom or other video URL."
                                className={`${inputClass} pl-10`}
                            />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                            Any video link works — we&apos;ll try to pull the thumbnail and length automatically for YouTube, Vimeo and Loom.
                        </p>
                        <div className="mt-2 rounded-xl border border-gray-200 bg-white flex items-center gap-3 px-4 py-3">
                            {thumbnailUrl ? (
                                <img
                                    src={thumbnailUrl}
                                    alt="Video thumbnail preview"
                                    className="w-14 h-14 rounded-md object-cover shrink-0"
                                />
                            ) : videoForm.link.trim() ? (
                                <div className="w-14 h-14 rounded-md shrink-0 bg-gray-100 flex items-center justify-center">
                                    <FaVideo className="text-gray-400" size={16} />
                                </div>
                            ) : (
                                <div
                                    className="w-14 h-14 rounded-md shrink-0 bg-gray-100"
                                    style={{
                                        backgroundImage:
                                            "repeating-linear-gradient(45deg, #d1d5db 0, #d1d5db 2px, transparent 2px, transparent 8px)",
                                    }}
                                />
                            )}
                            <div>
                                <p className="text-sm font-semibold text-gray-700">
                                    {thumbnailUrl ? "Thumbnail found" : videoForm.link.trim() ? "Link added" : "No link yet"}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {isFetchingDuration
                                        ? "Detecting length..."
                                        : thumbnailUrl
                                            ? "This preview will be used for the card"
                                            : videoForm.link.trim()
                                                ? "No preview available for this source — the link will still be saved"
                                                : "Thumbnail preview will appear here"}
                                </p>
                            </div>
                        </div>
                        <div className="hidden">
                            <div ref={playerContainerRef} />
                        </div>
                    </div>

                    <div>
                        <p className={labelClass}>Title</p>
                        <input
                            type="text"
                            value={videoForm.title}
                            onChange={(e) => setVideoForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="e.g. Bali Temple Etiquette & Dress Code"
                            className={inputClass}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className={labelClass}>Category</p>
                            <div className="relative">
                                <select
                                    value={videoForm.category}
                                    onChange={(e) => setVideoForm((f) => ({ ...f, category: e.target.value }))}
                                    className={`${inputClass} appearance-none pr-9`}
                                >
                                    {videoCategoryOptions.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                                <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
                            </div>
                        </div>
                        <div>
                            <p className={labelClass}>Duration (optional)</p>
                            <input
                                type="text"
                                value={videoForm.duration}
                                onChange={(e) => setVideoForm((f) => ({ ...f, duration: e.target.value }))}
                                placeholder={isFetchingDuration ? "Detecting..." : "4:12"}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div>
                        <p className={labelClass}>Status</p>
                        <div className="flex items-center gap-3 bg-p-gray rounded-xl px-4 py-3">
                            <ToggleInput
                                checked={videoForm.isActive}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setVideoForm((f) => ({ ...f, isActive: e.target.checked }))
                                }
                            />
                            <span className={`text-sm font-medium ${videoForm.isActive ? "text-emerald-600" : "text-gray-400"}`}>
                                {videoForm.isActive ? "Active" : "Inactive"}
                            </span>
                        </div>
                    </div>

                    <div>
                        <p className={labelClass}>Description</p>
                        <textarea
                            rows={3}
                            value={videoForm.description}
                            onChange={(e) => setVideoForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Short summary so the team knows what's inside..."
                            className={`${inputClass} resize-none`}
                        />
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <div
                        onClick={() => !isEditing && fileInputRef.current?.click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!isEditing) setIsDraggingOver(true);
                        }}
                        onDragLeave={() => setIsDraggingOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDraggingOver(false);
                            if (isEditing) return;
                            const file = e.dataTransfer.files?.[0];
                            if (!file) return;
                            const validationError = validateDocumentFile(file);
                            if (validationError) return Alert({ type: "error", message: validationError });
                            setDocumentForm((f) => ({ ...f, file, fileName: file.name }));
                        }}
                        className={`rounded-xl border border-dashed px-4 py-8 flex flex-col items-center justify-center text-center transition-colors ${isEditing
                            ? "cursor-not-allowed border-gray-200 bg-p-gray opacity-70"
                            : isDraggingOver
                                ? "cursor-pointer border-primary-red bg-primary-red/5"
                                : "cursor-pointer border-gray-300 bg-p-gray"
                            }`}
                    >
                        <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm mb-2">
                            <FaCloudUploadAlt className="text-gray-500" size={16} />
                        </span>
                        <p className="text-sm font-medium text-gray-700">
                            {isEditing
                                ? "Current file uploaded"
                                : documentForm.fileName || "Drag & drop a file, or click to browse"}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            {isEditing ? "The uploaded file can't be replaced here" : "PDF, DOCX, XLSX or PPTX · up to 25 MB"}
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.docx,.xlsx,.pptx"
                            className="hidden"
                            disabled={isEditing}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const validationError = validateDocumentFile(file);
                                if (validationError) {
                                    Alert({ type: "error", message: validationError });
                                    return;
                                }
                                setDocumentForm((f) => ({ ...f, file, fileName: file.name }));
                            }}
                        />
                    </div>

                    <div>
                        <p className={labelClass}>Title</p>
                        <input
                            type="text"
                            value={documentForm.title}
                            onChange={(e) => setDocumentForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="e.g. Tour Leader Handbook 2026"
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <p className={labelClass}>Description</p>
                        <textarea
                            rows={2}
                            value={documentForm.description}
                            onChange={(e) => setDocumentForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Short summary so the team knows what's inside..."
                            className={`${inputClass} resize-none`}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className={labelClass}>Category</p>
                            <div className="relative">
                                <select
                                    value={documentForm.category}
                                    onChange={(e) => setDocumentForm((f) => ({ ...f, category: e.target.value }))}
                                    className={`${inputClass} appearance-none pr-9`}
                                >
                                    {documentCategoryOptions.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                                <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
                            </div>
                        </div>
                        <div>
                            <p className={labelClass}>Status</p>
                            <div className="flex items-center gap-3 bg-p-gray rounded-xl px-4 py-3">
                                <ToggleInput
                                    checked={documentForm.isActive}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setDocumentForm((f) => ({ ...f, isActive: e.target.checked }))
                                    }
                                />
                                <span className={`text-sm font-medium ${documentForm.isActive ? "text-emerald-600" : "text-gray-400"}`}>
                                    {documentForm.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </FormModal>
    );
};

export default AddResourceModal;
